import { useState } from "react";
import { Mail, Phone, MapPin, MessageCircle, Send, Loader2, CheckCircle2 } from "lucide-react";
import { CONTACT } from "../data/mock";
import { toast } from "sonner";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error("Please fill all fields before sending");
      return;
    }
    setSubmitting(true);

    // Open WhatsApp with prefilled message — no backend needed
    const text = encodeURIComponent(
      `Hello Reptile Hisabbook,\n\nName: ${form.name}\nPhone: ${form.phone}\n\nMessage:\n${form.message}`
    );
    const whatsappUrl = `https://wa.me/${CONTACT.whatsapp}?text=${text}`;

    setTimeout(() => {
      window.open(whatsappUrl, "_blank", "noopener,noreferrer");
      setSubmitting(false);
      setSubmitted(true);
      toast.success("Opening WhatsApp — your message is ready to send!");
      setForm({ name: "", phone: "", message: "" });
      setTimeout(() => setSubmitted(false), 4000);
    }, 600);
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 md:py-28 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <span className="text-xs tracking-[0.22em] uppercase font-semibold text-teal-600">
            Get in touch
          </span>
          <h2
            data-testid="contact-headline"
            className="font-heading mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900"
          >
            Have a question? <span className="text-orange-500">We're listening.</span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
            Talk to our team about plans, demos, or anything you'd like to know
            about Reptile Hisabbook.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-stretch">
          {/* Contact info card */}
          <div className="lg:col-span-2 rounded-3xl bg-slate-900 text-white p-8 md:p-10 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-orange-500/20 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-teal-500/20 blur-3xl" />

            <div className="relative">
              <h3 className="font-heading text-2xl md:text-3xl font-bold">
                Reach us directly
              </h3>
              <p className="mt-3 text-slate-300 text-sm md:text-base">
                Our support team replies within working hours, every working day.
              </p>

              <ul className="mt-8 space-y-6">
                <li className="flex items-start gap-4">
                  <span className="h-11 w-11 rounded-xl bg-orange-500/15 text-orange-400 flex items-center justify-center shrink-0">
                    <Mail className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">
                      Email
                    </div>
                    <a
                      href={`mailto:${CONTACT.email}`}
                      data-testid="contact-email-link"
                      className="mt-1 inline-block font-semibold text-white hover:text-orange-400 transition-colors break-all"
                    >
                      {CONTACT.email}
                    </a>
                  </div>
                </li>

                <li className="flex items-start gap-4">
                  <span className="h-11 w-11 rounded-xl bg-teal-500/15 text-teal-300 flex items-center justify-center shrink-0">
                    <Phone className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">
                      Phone
                    </div>
                    <a
                      href={`tel:${CONTACT.phoneRaw}`}
                      data-testid="contact-phone-link"
                      className="mt-1 inline-block font-semibold text-white hover:text-teal-300 transition-colors"
                    >
                      +91 {CONTACT.phone}
                    </a>
                  </div>
                </li>

                <li className="flex items-start gap-4">
                  <span className="h-11 w-11 rounded-xl bg-emerald-500/15 text-emerald-300 flex items-center justify-center shrink-0">
                    <MessageCircle className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">
                      WhatsApp
                    </div>
                    <a
                      href={`https://wa.me/${CONTACT.whatsapp}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-whatsapp-link"
                      className="mt-1 inline-block font-semibold text-white hover:text-emerald-300 transition-colors"
                    >
                      Chat with us instantly
                    </a>
                  </div>
                </li>

                <li className="flex items-start gap-4">
                  <span className="h-11 w-11 rounded-xl bg-white/10 text-white flex items-center justify-center shrink-0">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">
                      Office
                    </div>
                    <p
                      data-testid="contact-address"
                      className="mt-1 font-medium text-slate-200 text-sm leading-relaxed"
                    >
                      {CONTACT.address}
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact form */}
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="lg:col-span-3 rounded-3xl bg-white border border-slate-200 p-8 md:p-10 shadow-sm"
          >
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
              Send us a message
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              We'll open WhatsApp with your message ready to go.
            </p>

            <div className="mt-7 space-y-5">
              <Field
                label="Full name"
                name="name"
                placeholder="e.g. Rajesh Kumar"
                value={form.name}
                onChange={handleChange}
                testid="contact-input-name"
              />
              <Field
                label="Phone number"
                name="phone"
                placeholder="e.g. 9876543210"
                value={form.phone}
                onChange={handleChange}
                type="tel"
                testid="contact-input-phone"
              />
              <div>
                <label className="block text-xs font-semibold tracking-[0.14em] uppercase text-slate-600 mb-2">
                  Your message
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  data-testid="contact-input-message"
                  placeholder="Tell us how we can help…"
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 focus:border-teal-500 focus:bg-white focus:ring-4 focus:ring-teal-500/15 transition-all outline-none resize-none text-slate-900 placeholder:text-slate-400"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 disabled:opacity-70 disabled:cursor-not-allowed px-6 py-4 text-sm md:text-base font-semibold text-white shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 transition-all active:scale-95"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Opening WhatsApp…
                  </>
                ) : submitted ? (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    Sent! Opening WhatsApp…
                  </>
                ) : (
                  <>
                    Send via WhatsApp
                    <Send className="h-4 w-4" />
                  </>
                )}
              </button>
              <p className="text-xs text-center text-slate-500">
                By submitting, you'll be redirected to WhatsApp with your message
                pre-filled.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, name, type = "text", placeholder, value, onChange, testid }) => (
  <div>
    <label
      htmlFor={name}
      className="block text-xs font-semibold tracking-[0.14em] uppercase text-slate-600 mb-2"
    >
      {label}
    </label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 focus:border-teal-500 focus:bg-white focus:ring-4 focus:ring-teal-500/15 transition-all outline-none text-slate-900 placeholder:text-slate-400"
    />
  </div>
);

export default Contact;
